import React, { useState, useEffect } from 'react';

const ContactSectionAdmin = () => {
  const [contactData, setContactData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    fetchContactData();
  }, []);

  const fetchContactData = async () => {
    try {
      const response = await fetch('https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/contact-section');
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch data');
      }
      
      setContactData(result.data);
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'danger' });
      console.error('Error fetching contact section data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactData({
      ...contactData,
      [name]: value
    });
  };

  const handleContactInfoChange = (index, field, value) => {
    const updatedContactInfo = [...contactData.contactInfo];
    updatedContactInfo[index] = {
      ...updatedContactInfo[index],
      [field]: value
    };
    setContactData({
      ...contactData,
      contactInfo: updatedContactInfo
    });
  };

  const addContactInfo = () => {
    const newContactInfo = {
      id: Date.now().toString(), // Simple unique id for new contact info
      iconClass: "",
      title: "",
      content: ""
    };
    setContactData({
      ...contactData,
      contactInfo: [...contactData.contactInfo, newContactInfo]
    });
  };

  const removeContactInfo = (index) => {
    if (contactData.contactInfo.length > 1) {
      const updatedContactInfo = [...contactData.contactInfo];
      updatedContactInfo.splice(index, 1);
      setContactData({
        ...contactData,
        contactInfo: updatedContactInfo
      });
    }
  };

  const handleFormFieldChange = (index, field, value) => {
    const updatedFormFields = [...contactData.formFields];
    updatedFormFields[index] = {
      ...updatedFormFields[index],
      [field]: value
    };
    setContactData({
      ...contactData,
      formFields: updatedFormFields
    });
  };

  const addFormField = () => {
    const newFormField = {
      name: "",
      label: "",
      type: "text",
      required: true,
      placeholder: "",
      colWidth: "col-md-12"
    };
    setContactData({
      ...contactData,
      formFields: [...contactData.formFields, newFormField]
    });
  };

  const removeFormField = (index) => {
    if (contactData.formFields.length > 1) {
      const updatedFormFields = [...contactData.formFields];
      updatedFormFields.splice(index, 1);
      setContactData({
        ...contactData,
        formFields: updatedFormFields
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await fetch('https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/contact-section', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactData),
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to update data');
      }
      
      setContactData(result.data);
      setMessage({ text: 'Contact section updated successfully!', type: 'success' });
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'danger' });
      console.error('Error updating contact section:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async () => {
    try {
      const response = await fetch('https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/contact-section/toggle', {
        method: 'PATCH',
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to toggle status');
      }
      
      setContactData(result.data);
      setMessage({ 
        text: `Contact section ${result.data.isActive ? 'activated' : 'deactivated'} successfully!`, 
        type: 'success' 
      });
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'danger' });
      console.error('Error toggling contact section status:', err);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset to default data? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch('https://ecommerce-product-05-19692c57dcd1.herokuapp.com/api/contact-section/reset', {
        method: 'POST',
      });

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to reset data');
      }
      
      setContactData(result.data);
      setMessage({ text: 'Contact section reset to default successfully!', type: 'success' });
    } catch (err) {
      setMessage({ text: `Error: ${err.message}`, type: 'danger' });
      console.error('Error resetting contact section:', err);
    }
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <h2 className="mb-4">Manage Contact Section</h2>
      
      {message.text && (
        <div className={`alert alert-${message.type}`} role="alert">
          {message.text}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title">Section Status</h5>
            <button 
              className={`btn ${contactData?.isActive ? 'btn-warning' : 'btn-success'}`}
              onClick={handleToggleActive}
            >
              {contactData?.isActive ? 'Deactivate Section' : 'Activate Section'}
            </button>
          </div>
          <p className="card-text">
            Current status: <span className={`badge bg-${contactData?.isActive ? 'success' : 'danger'}`}>
              {contactData?.isActive ? 'Active' : 'Inactive'}
            </span>
          </p>
          <button className="btn btn-outline-danger" onClick={handleReset}>
            Reset to Default
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Section Content</h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label htmlFor="sectionTitle" className="form-label">Section Title</label>
              <input
                type="text"
                className="form-control"
                id="sectionTitle"
                name="sectionTitle"
                value={contactData?.sectionTitle || ''}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="buttonText" className="form-label">Button Text</label>
              <input
                type="text"
                className="form-control"
                id="buttonText"
                name="buttonText"
                value={contactData?.buttonText || ''}
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Contact Information</h5>
            <button type="button" className="btn btn-sm btn-primary" onClick={addContactInfo}>
              Add Contact Info
            </button>
          </div>
          <div className="card-body">
            {contactData?.contactInfo.map((info, index) => (
              <div key={info.id} className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Contact Info #{index + 1}</h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeContactInfo(index)}
                    disabled={contactData.contactInfo.length <= 1}
                  >
                    Remove
                  </button>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Icon Class</label>
                      <input
                        type="text"
                        className="form-control"
                        value={info.iconClass}
                        onChange={(e) => handleContactInfoChange(index, 'iconClass', e.target.value)}
                        placeholder="e.g., ti-map"
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={info.title}
                        onChange={(e) => handleContactInfoChange(index, 'title', e.target.value)}
                        placeholder="Contact title"
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Content</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={info.content}
                        onChange={(e) => handleContactInfoChange(index, 'content', e.target.value)}
                        placeholder="Contact content (HTML allowed)"
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* <div className="card mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Form Fields</h5>
            <button type="button" className="btn btn-sm btn-primary" onClick={addFormField}>
              Add Form Field
            </button>
          </div>
          <div className="card-body">
            {contactData?.formFields.map((field, index) => (
              <div key={index} className="card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Form Field #{index + 1}</h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => removeFormField(index)}
                    disabled={contactData.formFields.length <= 1}
                  >
                    Remove
                  </button>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Field Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={field.name}
                        onChange={(e) => handleFormFieldChange(index, 'name', e.target.value)}
                        placeholder="e.g., name"
                        required
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Label</label>
                      <input
                        type="text"
                        className="form-control"
                        value={field.label}
                        onChange={(e) => handleFormFieldChange(index, 'label', e.target.value)}
                        placeholder="Field label"
                        required
                      />
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Type</label>
                      <select
                        className="form-select"
                        value={field.type}
                        onChange={(e) => handleFormFieldChange(index, 'type', e.target.value)}
                      >
                        <option value="text">Text</option>
                        <option value="email">Email</option>
                        <option value="tel">Phone</option>
                        <option value="textarea">Textarea</option>
                      </select>
                    </div>
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Column Width</label>
                      <select
                        className="form-select"
                        value={field.colWidth}
                        onChange={(e) => handleFormFieldChange(index, 'colWidth', e.target.value)}
                      >
                        <option value="col-md-6">Half Width</option>
                        <option value="col-md-12">Full Width</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={field.required}
                      onChange={(e) => handleFormFieldChange(index, 'required', e.target.checked)}
                      id={`required-${index}`}
                    />
                    <label className="form-check-label" htmlFor={`required-${index}`}>
                      Required field
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div> */}

        <div className="d-flex justify-content-between">
          <button type="button" className="btn btn-secondary" onClick={fetchContactData}>
            Reset Changes
          </button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactSectionAdmin;